import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';

import {
  TDropdownItem,
  TDropdownOffset,
  TDropdownProps,
} from '@treezor/design-system/build/components/Dropdown/Dropdown.types';
import { Box, Divider, DropdownItem, Portal } from '@treezor/design-system';

export type TAdvancedDropdownItem = {
  title?: string;
  subItems: TDropdownItem[];
};

export type TAdvancedDropdown = Omit<TDropdownProps, 'items'> & {
  items: TAdvancedDropdownItem[];
};

export const AdvancedDropdown = forwardRef<HTMLDivElement, TAdvancedDropdown>(
  (
    {
      items,
      children,
      noOptionsComponent,
      size = 'm',
      onClickItem,
      onClose,
      closeOnSelect = true,
      className,
      contentClassName,
      parentRef,
      withSearch,
      overflow = false,
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const [position, setPosition] = useState<TDropdownOffset>();
    const [minWidth, setMinWidth] = useState<number>(0);

    useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(ref, () => containerRef.current);

    const handleItemClick = useCallback(
      (index: number) => {
        onClickItem?.(index);
        if (closeOnSelect) {
          onClose?.();
        }
      },
      [onClickItem, onClose, closeOnSelect],
    );

    /**
     * Determine dropdown position with parent & viewport
     */
    useLayoutEffect(() => {
      const parentRect = parentRef?.current?.getBoundingClientRect();
      const dropdownRect = containerRef?.current?.getBoundingClientRect();

      let top = parentRect ? parentRect.top + parentRect.height : 0;
      let bottom = undefined;
      let left = parentRect?.left;
      let right;

      // Check if dropdown is outside the viewport
      if (dropdownRect && parentRect) {
        // Outside bottom of viewport
        if (dropdownRect.height + top > window.innerHeight) {
          top = undefined;
          bottom = window.innerHeight - parentRect.top;
        } else {
          bottom = undefined;
        }

        // Outside right of viewport
        if (dropdownRect.width + left > window.innerWidth) {
          left = undefined;
          right = window.innerWidth - parentRect.right;
        }
      }

      setPosition({
        bottom,
        top,
        left,
        right,
      });
      setMinWidth(parentRect?.width ?? 0);
    }, [parentRef]);

    /**
     * Close dropdown on window resize
     */
    useEffect(
      () => {
        function handleResize() {
          onClose?.();
        }

        window.addEventListener('resize', handleResize);

        return () => {
          window.removeEventListener('resize', handleResize);
        };
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    return (
      <Portal>
        <div className="fixed z-overlay top-0 left-0 right-0 bottom-0" role="presentation">
          <div aria-hidden={true} className="fixed top-0 left-0 right-0 bottom-0" onClick={onClose} />
          <div
            ref={containerRef}
            className={clsx('absolute select-none', className)}
            style={{
              top: position?.top,
              left: position?.left,
              right: position?.right,
              bottom: position?.bottom,
              minWidth,
            }}
          >
            <div className="py-1">
              <Box shadow="100" contentClassName={clsx('p-0 max-h-[360px]', contentClassName)}>
                {children ? (
                  <div className={overflow ? 'overflow-visible' : 'overflow-auto'}>{children}</div>
                ) : (
                  <>
                    <>
                      {!items?.length && !!noOptionsComponent ? (
                        noOptionsComponent
                      ) : (
                        <div className={clsx(!withSearch && 'py-4 flex flex-col items-stretch gap-3')}>
                          {items?.map((item, index) => (
                            <>
                              <div key={`sub-item-${index}`}>
                                {item.subItems.map((subItem, subItemIndex) => (
                                  <DropdownItem
                                    key={`subItem-${subItemIndex}`}
                                    size={size}
                                    {...subItem}
                                    onClick={() => {
                                      subItem.onClick?.();
                                      handleItemClick(subItemIndex);
                                    }}
                                  />
                                ))}
                              </div>
                              {index !== items.length - 1 && (
                                <Divider thickness="100" direction="horizontal" key={`divider-item-${index}`} />
                              )}
                            </>
                          ))}
                        </div>
                      )}
                    </>
                  </>
                )}
              </Box>
            </div>
          </div>
        </div>
      </Portal>
    );
  },
);

AdvancedDropdown.displayName = 'AdvancedDropdown';
